import { CheckCircle2 } from 'lucide-react';
import type { ReactNode } from 'react';
import type { AnyFieldApi } from '@tanstack/react-form';

const FONT_SANS = 'var(--font-brand)';

/**
 * When a form field is allowed to look wrong, and how it says what is wrong.
 *
 * THE BEHAVIOUR THIS REPLACES. Every field on /login and /signup validated
 * `onBlur` against a schema whose first rule is `.min(1, 'Email is required')`,
 * and painted its border `#ff4d4f` whenever `errors.length > 0`. So clicking
 * into a field and clicking back out — or simply tabbing through the form to
 * see it — turned it red. The field was marked wrong for having been VISITED,
 * before the user had done anything at all. On a sign-in screen that is the
 * first thing a new user sees.
 *
 * Worse, the message was thrown away. `field.state.meta.errors` fed the border
 * colour and nothing else, so the user got a red box with no text: no way to
 * tell "required" from "that is not a valid email" from a server rejection.
 * Colour was the only carrier, which also fails WCAG 1.4.1 (use of colour) and
 * 3.3.1 (error identification) — a screen reader got nothing whatsoever.
 *
 * THE RULE. An empty field the user has not typed in is not yet wrong; it is
 * unfinished. So:
 *
 *   - visited and left empty        -> nothing. They may be coming back to it.
 *   - typed in, then blurred        -> validate. "not a valid email" is worth
 *                                     saying the moment it is true.
 *   - submitted                     -> validate everything, including empty.
 *                                     This is the point at which "required"
 *                                     becomes a real claim about the form.
 *
 * The confirm-password field already worked this way — it guarded on a
 * `started` flag and rendered `errors[0]` — so this generalises the one field
 * that had it right rather than inventing a new convention.
 *
 * The validators are left alone on purpose. They still produce the error on
 * blur, because submit needs it; this only decides when it is fair to SHOW it.
 * Keeping validation and presentation separate means a field cannot end up
 * submittable-but-silent.
 */
export function shouldShowFieldError(
  meta: { errors: Array<unknown>; isDirty?: boolean },
  submissionAttempts: number,
): boolean {
  if (meta.errors.length === 0) return false;
  return Boolean(meta.isDirty) || submissionAttempts > 0;
}

/**
 * Retract a standing error the moment the field's value changes.
 *
 * THE BUG THIS FIXES. Errors are stored per TRIGGER: `validateSync(cause)`
 * writes only `errorMap[cause]` and leaves the other slots untouched. The auth
 * fields validate `onBlur` and `onSubmit`, so once either of those slots was
 * filled, nothing short of another blur or submit could empty it. The value
 * underneath was free to change without the message ever being reconsidered.
 *
 * Picking saved credentials hit this every time. A password manager fills both
 * boxes at once and focuses neither, so no blur and no submit followed — and
 * the form went on printing "Email is required" and "Password is required"
 * under two fields it had just filled. The values DID reach the form state and
 * pressing "Log In" did sign in; the block was entirely imaginary. But nobody
 * presses a button under two red errors, so in practice it stopped people at
 * the door, and the one thing it stopped was the ordinary path of returning
 * with a remembered password.
 *
 * Submitting an empty form and then typing by hand had the same shape: the
 * "required" pair stayed put for the whole time it took to fill them in.
 *
 * THE RULE. Changing a field withdraws whatever we were claiming about it. The
 * claim was made about a value that no longer exists, so it is not wrong or
 * right any more — it is void. The next blur or submit forms a new one.
 *
 * The alternative was to add an `onChange` validator so the message stays live.
 * Rejected: it puts a SECOND slot in the map without clearing the first, so a
 * blur error and a change error can disagree and the stale one wins the render;
 * and it re-introduces exactly the mid-typing correction this flow was fixed to
 * stop, telling someone their address is invalid while they are on its second
 * letter. Going quiet while a field is in flight says less and says it later,
 * which is the right trade on a sign-in form.
 */
export function clearErrorsOnChange({ fieldApi }: { fieldApi: AnyFieldApi }) {
  // Guarded: writing the map on every keystroke would publish a store update
  // per character to every subscriber of this field.
  if (fieldApi.state.meta.errors.length === 0) return;
  // Every slot named explicitly, because `setErrorMap` MERGES —
  // `{ ...prev.errorMap, ...next }` — so `setErrorMap({})` spreads nothing over
  // the old map and silently changes nothing at all. Only an explicit
  // `undefined` per key actually empties it.
  fieldApi.setErrorMap({
    onChange: undefined,
    onBlur: undefined,
    onSubmit: undefined,
    onMount: undefined,
    onServer: undefined,
    onDynamic: undefined,
  });
}

/** First error for a field, as a string, or null when there is nothing to say. */
export function firstFieldError(meta: {
  errors: Array<unknown>;
}): string | null {
  for (const error of meta.errors) {
    if (typeof error === 'string' && error) return error;
    // Zod issues arrive as objects; the schemas here all set a message.
    if (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof (error as { message?: unknown }).message === 'string'
    ) {
      return (error as { message: string }).message;
    }
  }
  return null;
}

export interface FieldErrorProps {
  /** Ties the message to its input via aria-describedby. */
  id: string;
  children: ReactNode;
}

/**
 * The message under a field.
 *
 * `role="alert"` so it is announced when it appears rather than only being
 * there for anyone who happens to navigate onto it.
 */
export function FieldError({ id, children }: FieldErrorProps) {
  return (
    <p
      id={id}
      role="alert"
      className="text-[#ff4d4f] text-[13px] font-medium"
      style={{ fontFamily: FONT_SANS }}
    >
      {children}
    </p>
  );
}

export interface AuthSuccessBannerProps {
  children: ReactNode;
  /** Extra layout classes for the call site — spacing, width. */
  className?: string;
}

/**
 * The form-level SUCCESS banner: a reset link sent, a password changed.
 *
 * The green counterpart of `AuthErrorBanner`, same geometry — 12px radius, 14px
 * type, soft fill and border — so a confirmation and a refusal occupy the same
 * slot and read as the same kind of message.
 *
 * WHY THIS EXISTS AT ALL. Three flows ended by dropping the user on a bare
 * /login with nothing said:
 *
 *   - "Send reset link" navigated to /login the moment the mail went out. The
 *     user could not tell whether anything had been sent, and the reasonable
 *     response to that is to go back and press it again.
 *   - Setting a new password through the reset link did the same.
 *   - Changing a password in the profile signs the user out — correctly, since
 *     a password change should end other sessions — and landed them on /login
 *     with no idea it had worked. PasswordCard had a green banner for this, but
 *     it was wired to `const [success] = useState(false)`: no setter, so it
 *     could never render. That reads like an abandoned attempt, and the reason
 *     it was abandoned is structural — a banner on the page you are being
 *     signed OUT of has a few milliseconds to live. The confirmation has to be
 *     on the destination, not the origin.
 *
 * `role="status"` rather than `alert`: this is confirmation, not a problem, and
 * `alert` is assertive — it interrupts a screen reader mid-sentence. `status`
 * is announced politely when the user reaches a pause.
 *
 * The check icon carries no information the text does not, so it is
 * `aria-hidden` and the sentence stands alone.
 */
export function AuthSuccessBanner({
  children,
  className,
}: AuthSuccessBannerProps) {
  return (
    <div
      role="status"
      className={`flex items-start gap-2.5 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-[14px] font-medium text-green-700 ${className ?? ''}`}
      style={{ fontFamily: FONT_SANS }}
    >
      <CheckCircle2 className="mt-px h-5 w-5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

export interface AuthErrorBannerProps {
  children: ReactNode;
  /** Extra layout classes for the call site — spacing, width. */
  className?: string;
}

/**
 * The form-level error banner: a rejected sign-in, an address already taken, a
 * method the project has not enabled.
 *
 * Soft fill, soft border, dark red text — the treatment the MFA enrolment card
 * already used. The auth screens each carried their own copy in the opposite
 * key: a hard `#ff4d4f` 1px border on WHITE, which reads as an alarm outline
 * rather than a message, and sat oddly beside the quiet surfaces around it.
 * Five files had that same copy.
 *
 * `role="alert"` is the part none of those five had. The banner appears in
 * response to a submit, so it has to be announced when it does; without it a
 * screen-reader user submits, hears nothing, and has no way to know the form
 * refused them.
 *
 * Keeps the auth surface's own geometry — 12px radius, 14px type — rather than
 * the enrolment card's `rounded-lg`/`text-sm`, so it matches the fields and
 * buttons it sits between.
 */
export function AuthErrorBanner({ children, className }: AuthErrorBannerProps) {
  return (
    <div
      role="alert"
      className={`rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[14px] font-medium text-red-700 ${className ?? ''}`}
      style={{ fontFamily: FONT_SANS }}
    >
      {children}
    </div>
  );
}
